/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { postRequest, getRequest, deleteRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import { FaTrash, FaFilePdf, FaUpload, FaQuestionCircle } from 'react-icons/fa';

const ManageFaqFiles = () => {
    const [language, setLanguage] = useState('');
    const [file, setFile] = useState(null);
    const [filesList, setFilesList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    const fetchFaqFiles = async () => {
        try {
            setFetching(true);
            const res = await getRequest('faq-files');
            setFilesList(res?.data || []);
        } catch (err) {
            console.error('Error fetching FAQ files:', err);
            toast.error("Failed to load FAQ files list.");
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        fetchFaqFiles();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!language) {
            toast.error("Please select a language (Hindi/English)");
            return;
        }

        if (!file) {
            toast.error("Please select a PDF file to upload");
            return;
        }

        const formData = new FormData();
        formData.append('language', language);
        formData.append('file', file);

        setLoading(true);
        try {
            await postRequest('faq-files', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(`FAQ PDF for ${language} uploaded successfully!`);
            setFile(null);
            setLanguage('');
            // Reset file input value
            document.getElementById('faqFileInput').value = '';
            fetchFaqFiles();
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to upload FAQ PDF.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this FAQ PDF?")) {
            try {
                await deleteRequest(`faq-files/${id}`);
                toast.success("FAQ PDF deleted successfully!");
                fetchFaqFiles();
            } catch (err) {
                console.error(err);
                toast.toast.error("Failed to delete FAQ PDF.");
            }
        }
    };

    return (
        <div className="container-fluid mt-4">
            {/* Header */}
            <div className="dashboard-header-card d-flex align-items-center justify-content-between mb-4 mx-2 shadow-lg" style={{ background: 'linear-gradient(135deg, #0054A6 0%, #007BFF 100%)', borderRadius: '15px', padding: '1.5rem 2rem' }}>
                <div className="d-flex align-items-center">
                    <div className="icon-circle mr-3" style={{ background: 'rgba(255,255,255,0.2)', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FaQuestionCircle className="text-white" style={{ fontSize: '24px' }} />
                    </div>
                    <div>
                        <h3 className="text-white font-weight-bold mb-0" style={{ letterSpacing: '0.5px' }}>Manage FAQ PDFs</h3>
                        <p className="text-white-50 mb-0 small">Upload and manage FAQ documents for Hindi and English versions</p>
                    </div>
                </div>
            </div>

            <div className="row mx-1">
                {/* Upload Form */}
                <div className="col-lg-5 mb-4">
                    <div className="card shadow border-0" style={{ borderRadius: '15px' }}>
                        <div className="card-header bg-light border-0 py-3">
                            <h5 className="mb-0 font-weight-bold text-dark"><FaUpload className="mr-2 text-primary" /> Upload/Replace FAQ PDF</h5>
                        </div>
                        <div className="card-body p-4">
                            <form onSubmit={handleSubmit}>
                                <div className="form-group mb-4">
                                    <label className="font-weight-bold text-dark">Language Category <span className="text-danger">*</span></label>
                                    <select 
                                        className="form-control" 
                                        value={language} 
                                        onChange={(e) => setLanguage(e.target.value)}
                                        style={{ borderRadius: '8px', border: '1px solid #ced4da' }}
                                    >
                                        <option value="">-- Select Language --</option>
                                        <option value="Hindi">Hindi</option>
                                        <option value="English">English</option>
                                    </select>
                                </div>
                                <div className="form-group mb-4">
                                    <label className="font-weight-bold text-dark">PDF File <span className="text-danger">*</span></label>
                                    <input 
                                        type="file" 
                                        id="faqFileInput"
                                        className="form-control-file" 
                                        accept="application/pdf"
                                        onChange={(e) => setFile(e.target.files[0])}
                                    />
                                    <small className="form-text text-muted mt-2">Uploading for an existing language will automatically replace the old file.</small>
                                </div>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary btn-block py-2.5 font-weight-bold shadow-sm"
                                    disabled={loading}
                                    style={{ borderRadius: '8px', background: 'linear-gradient(135deg, #0054A6 0%, #007BFF 100%)', border: 'none' }}
                                >
                                    {loading ? 'Uploading...' : 'Upload PDF'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>

                {/* List Table */}
                <div className="col-lg-7 mb-4">
                    <div className="card shadow border-0" style={{ borderRadius: '15px' }}>
                        <div className="card-header bg-light border-0 py-3">
                            <h5 className="mb-0 font-weight-bold text-dark"><FaFilePdf className="mr-2 text-danger" /> Current Uploaded PDFs</h5>
                        </div>
                        <div className="card-body p-0">
                            {fetching ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : filesList.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <FaFilePdf size={40} className="mb-2 opacity-50" />
                                    <p className="mb-0">No FAQ PDFs uploaded yet.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="thead-light">
                                            <tr>
                                                <th className="px-4">Language</th>
                                                <th>Updated At</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filesList.map((fileItem) => {
                                                const fileUrl = `${process.env.REACT_APP_IMG_URL || 'http://localhost:8080'}/faq/${fileItem.file_url}`;
                                                return (
                                                    <tr key={fileItem.id}>
                                                        <td className="px-4 font-weight-bold text-primary">{fileItem.language}</td>
                                                        <td>{new Date(fileItem.updated_at).toLocaleString()}</td>
                                                        <td className="text-center">
                                                            <a 
                                                                href={fileUrl} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer" 
                                                                className="btn btn-sm btn-outline-danger mr-2"
                                                                title="View PDF"
                                                            >
                                                                <FaFilePdf /> View
                                                            </a>
                                                            <button 
                                                                onClick={() => handleDelete(fileItem.id)} 
                                                                className="btn btn-sm btn-danger"
                                                                title="Delete"
                                                            >
                                                                <FaTrash /> Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageFaqFiles;
